// CountryReport: one state at a date, for the country panel and diplomacy screen.
//
//   leaders       terms in force at D
//   memberships   bloc memberships in force at D, any status (member, partner, suspended, …)
//   wars          wars live at D where the state is a side or hosts fighting
//   relation      to the leader's state (relations.ts)
//   stats         every indicator's latest value ≤ year(D), with its year, unit and the sources of that
//                 datum (indicators.ts sourcesFor), plus the
//                 aliases the UI reads: gdp, population, hard_power (share of world military spending)
//   statements    the state's own statements dated ≤ D, newest first
//   grievances    sanctions, tariffs, wars, strikes and occupations against the leader's state (grievances.ts)

import type { Country, DateStr, LeaderTerm, Membership, Statement, War, WorldState } from '../core/schema.ts';
import { inSpan, yearOf } from '../core/dates.ts';
import type { CountryReport, RelationInfo } from './api.ts';
import { grievancesBetween } from './grievances.ts';
import { latest, shareAt, sourcesFor, totalSourcesOf, unitOf, type IndicatorIndex } from './indicators.ts';
import type { Diplo } from './relations.ts';
import { byDate, uniq } from './util.ts';
import { liveWars } from './wars.ts';

export interface ReportInputs {
  world: WorldState;
  ix: IndicatorIndex;
  diplo: Diplo;
  /** The leader's state (iso3): the other side of every grievance. */
  self: string;
  blocName(id: string): string;
}

/** Indicator behind each alias in CountryReport.stats. */
const GDP = 'gdp_usd';
const POPULATION = 'pop';
const MILEX = 'milex_usd';
const HARD_POWER_UNIT = 'share of world military spending';

function leadersAt(terms: LeaderTerm[], iso3: string, date: DateStr): CountryReport['leaders'] {
  return terms
    .filter((t) => t.country === iso3 && inSpan(t, date))
    .map((t) => ({ role: t.role, name: t.name, since: t.from, sources: t.sources }));
}

function membershipsAt(r: ReportInputs, list: Membership[], iso3: string, date: DateStr): CountryReport['memberships'] {
  return list
    .filter((m) => m.member === iso3 && inSpan(m, date))
    .map((m) => ({ bloc: r.blocName(m.bloc), status: m.status, since: m.from, sources: m.sources }));
}

function involves(w: War, iso3: string): boolean {
  return w.sides.a.includes(iso3) || w.sides.b.includes(iso3) || w.theatre.includes(iso3);
}

function warsAt(wars: War[], iso3: string, date: DateStr): War[] {
  return liveWars(wars, date).filter((w) => involves(w, iso3));
}

function statsAt(ix: IndicatorIndex, iso3: string, year: number): CountryReport['stats'] {
  const out: CountryReport['stats'] = {};
  for (const id of Object.keys(ix.ind.series)) {
    const hit = latest(ix, id, iso3, year);
    if (!hit) {
      continue;
    }
    out[id] = { value: hit[1], year: hit[0], unit: unitOf(ix, id), sources: sourcesFor(ix, id, iso3, hit[0]) };
  }

  // The UI's fixed names: current US$, people, and the 0..1 share of world military spending.
  for (const [alias, id] of [['gdp', GDP], ['population', POPULATION]] as const) {
    if (out[id]) {
      out[alias] = out[id];
    }
  }
  const power = shareAt(ix, MILEX, iso3, year);
  if (power) {
    out.hard_power = { value: power.share, year: power.year, unit: HARD_POWER_UNIT, sources: uniq([...sourcesFor(ix, MILEX, iso3, power.year), ...totalSourcesOf(ix, MILEX, power.year)]) };
  }
  return out;
}

function statementsAt(list: Statement[], iso3: string, date: DateStr): Statement[] {
  return list.filter((s) => s.actor === iso3 && s.date <= date).sort((a, b) => byDate(b.date, a.date));
}

export function countryReport(r: ReportInputs, country: Country, date: DateStr, relation: RelationInfo): CountryReport {
  const iso3 = country.iso3;
  return {
    country,
    leaders: leadersAt(r.world.leaders, iso3, date),
    memberships: membershipsAt(r, r.world.memberships, iso3, date),
    wars: warsAt(r.world.wars, iso3, date),
    relation,
    stats: statsAt(r.ix, iso3, yearOf(date)),
    statements: statementsAt(r.world.statements, iso3, date),
    grievances: grievancesBetween(r.diplo, r.world.control, r.self, iso3, date),
  };
}
